import React, { useState, useContext, useEffect } from 'react';
import { 
  Container, Typography, Box, TextField, MenuItem, Button, 
  Grid, FormControlLabel, Checkbox, Paper, Alert, Snackbar, CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Axios from 'axios';
import AxiosInstance from '../../AxiosInstance';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import StateContext from '../../Contexts/StateContext';
import { MasterCategories, CompanyCategories, DayLabourerCategories } from '../../Constants/BusinessOptions';
import LocationSelector from '../Common/LocationSelector';
import config, { environment } from '../../config';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function MapEvents({ setCoordinates }) {
    const map = useMap();
    useEffect(() => {
        map.on('click', (e) => {
            setCoordinates(e.latlng.lat, e.latlng.lng);
        });
    }, [map, setCoordinates]);
    return null;
}

function MapFlyTo({ location }) {
    const map = useMap();
    useEffect(() => {
        if (location[0] && location[1]) {
            map.flyTo(location, 13);
        }
    }, [location, map]);
    return null;
}

function UpdateBusiness() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const GlobalState = useContext(StateContext);

  const [formData, setFormData] = useState({
    business_type: 'Master',
    name: '',
    description: '',
    phone_number: '',
    email: '',
    website: '',
    facebook_url: '',
    hourly_rate: '',
    fixed_price: '',
    is_urgent: false,
    brings_tools: false,
    capacity: 1,
    category: '',
    address: '',
    szek: '',
    kozsegvaros: '',
    telepules: '',
    latitude: 45.8643,
    longitude: 25.7848,
    geocodingError: '',
    geocodingSuccess: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await AxiosInstance.get(`${config[environment].businessesEndpoint}${id}/`);
        const data = response.data;
        
        // Ha nem az övé, navigáljunk el (biztonsági mentés, bár a gombot sem látja)
        if (data.owner && GlobalState.userId && String(data.owner) !== String(GlobalState.userId)) {
          navigate('/');
          return;
        }

        // We use the names for location fields to match LocationSelector's non-Csango behavior
        const szekName = data.szek_name || data.szek || '';
        const kozsegName = data.kozsegvaros_name || data.kozsegvaros || '';
        const telepulesName = data.telepules_name || data.telepules || '';

        setFormData({
          ...data,
          szek: szekName,
          kozsegvaros: kozsegName,
          telepules: telepulesName,
          hourly_rate: data.hourly_rate || '',
          fixed_price: data.fixed_price || '',
          latitude: parseFloat(data.latitude) || 45.8643,
          longitude: parseFloat(data.longitude) || 25.7848,
          geocodingError: '',
          geocodingSuccess: false
        });
      } catch (err) {
        setError(t('Common.ErrorLoading', 'Hiba történt az adatok betöltésekor.'));
      } finally {
        setLoading(false);
      }
    };

    if (id && GlobalState.userId) {
      fetchData();
    }
  }, [id, GlobalState.userId, navigate, t]);

  const businessTypes = [
    { value: 'Master', label: t('SubMenu.Masters', 'Mester') },
    { value: 'Company', label: t('SubMenu.Companies', 'Cég') },
    { value: 'DayLabourer', label: t('SubMenu.DayLabourers', 'Napszámos') }
  ];

  const getCategories = () => {
    let categories = [];
    switch (formData.business_type) {
      case 'Master': categories = [...MasterCategories]; break;
      case 'Company': categories = [...CompanyCategories]; break;
      case 'DayLabourer': categories = [...DayLabourerCategories]; break;
      default: categories = [];
    }
    return categories
      .map(cat => ({ ...cat, translatedLabel: cat.label.includes('.') ? t(cat.label, cat.label) : cat.label }))
      .sort((a, b) => a.translatedLabel.localeCompare(b.translatedLabel, i18n.language));
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGeocode = async () => {
    if (!formData.address) return;
    try {
      const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
          geocodingSuccess: true,
          geocodingError: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          geocodingError: t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.'),
          geocodingSuccess: false
        }));
      }
    } catch (e) {
      setFormData(prev => ({
        ...prev,
        geocodingError: t('Thematic.GeocodingError', 'Hiba történt a keresés során.'),
        geocodingSuccess: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = { ...formData };

      // Segédmezők eltávolítása
      delete payload.geocodingError;
      delete payload.geocodingSuccess;
      
      // Read-only / Server-side mezők eltávolítása
      delete payload.id;
      delete payload.owner;
      delete payload.owner_username;
      delete payload.date_posted;
      delete payload.average_rating;
      delete payload.reviews;
      delete payload.availabilities;
      delete payload.type; 
      delete payload.linked_master_details;
      delete payload.average_rating_display;
      delete payload.location;
      delete payload.picture;
      delete payload.is_active;
      delete payload.featured;
      delete payload.created_at;
      delete payload.updated_at;

      // Helyszín nevek eltávolítása (csak az ID-kat/string értékeket küldjük szek, kozsegvaros, telepules mezőkben)
      delete payload.szek_name;
      delete payload.kozsegvaros_name;
      delete payload.telepules_name;

      // Egyéb számított vagy felesleges mezők (ha vannak)
      delete payload.business_type_display;
      delete payload.business_type; // Gyakran csak olvasható PATCH esetén

      // Koordináták és helyszínek formázása
      const lat = parseFloat(formData.latitude);
      const lon = parseFloat(formData.longitude);
      
      payload.szek = String(formData.szek || '');
      payload.kozsegvaros = String(formData.kozsegvaros || '');
      payload.telepules = String(formData.telepules || '');
      payload.latitude = !isNaN(lat) ? lat.toFixed(6) : null;
      payload.longitude = !isNaN(lon) ? lon.toFixed(6) : null;

      try {
        await AxiosInstance.patch(`${config[environment].businessesEndpoint}${id}/update/`, payload);
      } catch (patchErr) {
        if (patchErr.response && (patchErr.response.status === 405 || patchErr.response.status === 404)) {
          try {
            await AxiosInstance.patch(`${config[environment].businessesEndpoint}${id}/`, payload);
          } catch (retryErr) {
            if (retryErr.response && retryErr.response.status === 405) {
              await AxiosInstance.put(`${config[environment].businessesEndpoint}${id}/`, payload);
            } else {
              throw retryErr;
            }
          }
        } else {
          throw patchErr;
        }
      }
      setSuccess(true);
      setTimeout(() => {
        // Visszairányítás a megfelelő listára a típus alapján
        const typePath = formData.business_type === 'Master' ? 'mesterek' : 
                         formData.business_type === 'Company' ? 'cegek' : 'napszamosok';
        navigate(`/${i18n.language}/${typePath}`);
      }, 2000);
    } catch (err) {
      const errorData = err.response?.data;
      let errorMsg = "";
      if (errorData && typeof errorData === 'object') {
        if (errorData.detail && typeof errorData.detail === 'string') {
          errorMsg = errorData.detail;
        } else {
          errorMsg = JSON.stringify(errorData);
        }
      } else {
        errorMsg = err.message || t('Common.ErrorSaving', 'Hiba történt a mentés során.');
      }
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, mt: '50px' }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
          {t('Business.UpdateTitle', 'Adatok szerkesztése')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {String(error)}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{t('Common.SaveSuccess', 'Sikeres mentés!')}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t('Common.Type', 'Típus')}
                name="business_type"
                value={formData.business_type}
                onChange={handleChange}
                required
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {businessTypes.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t('Common.Category', 'Kategória')}
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {getCategories().map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.translatedLabel}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('Common.Name', 'Név / Megnevezés')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('Common.Bio', 'Bemutatkozás')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Administration.PhoneNumber', 'Telefonszám')}
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Administration.Email', 'Email cím')}
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Business.Website', 'Honlap')}
                name="website"
                value={formData.website || ''}
                onChange={handleChange}
                placeholder="https://www.weboldal.hu"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('SubMenu.FacebookUrl', 'Facebook profil URL')}
                name="facebook_url"
                value={formData.facebook_url || ''}
                onChange={handleChange}
              />
            </Grid>

            <LocationSelector
              selectedSzek={formData.szek}
              selectedKozseg={formData.kozsegvaros}
              selectedTelepules={formData.telepules}
              onSzekChange={(val) => setFormData(prev => ({ ...prev, szek: val, kozsegvaros: '', telepules: '' }))}
              onKozsegChange={(val) => setFormData(prev => ({ ...prev, kozsegvaros: val, telepules: '' }))}
              onTelepulesChange={(val) => setFormData(prev => ({ ...prev, telepules: val }))}
            />

            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                label={t('Common.Address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGeocode}
                sx={{ height: '56px' }}
              >
                {t('Thematic.Geocode')}
              </Button>
            </Grid>
            {formData.geocodingError && (
              <Grid item xs={12}>
                <Alert severity="error">{formData.geocodingError}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Common.Location')}</Typography>
              <Box sx={{ height: '300px', width: '100%', mb: 2 }}>
                <MapContainer center={[formData.latitude, formData.longitude]} zoom={13} style={{ height: '100%', borderRadius: '8px' }}>
                  <TileLayer url={config[environment].osmTiles} />
                  <Marker position={[formData.latitude, formData.longitude]} icon={defaultIcon} />
                  <MapEvents setCoordinates={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))} />
                  <MapFlyTo location={[formData.latitude, formData.longitude]} />
                </MapContainer>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('Business.HourlyRate')}
                name="hourly_rate"
                value={formData.hourly_rate}
                onChange={handleChange}
              />
            </Grid>
            {formData.business_type === 'DayLabourer' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('Business.Capacity')}
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="brings_tools" checked={formData.brings_tools} onChange={handleChange} />}
                label={t('Business.BringsTools', 'Hoz szerszámot')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="is_urgent" checked={formData.is_urgent} onChange={handleChange} />}
                label={t('Business.UrgentWork', 'Vállal sürgős munkát')}
              />
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              size="large" 
              disabled={saving}
              sx={{ bgcolor: 'var(--sznodarkblue)' }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : t('Common.Save', 'Mentés')}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
              {t('Common.Cancel', 'Mégse')}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          {t('Business.UpdateSuccess', 'Sikeresen módosítottad az adatokat!')}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default UpdateBusiness;
